import type { ICreateUserCmd } from "@/data/commands/user/CreateUserCmd";
import { Factory } from "@/data/dao/Factory";
import { UserDao } from "@/data/dao/service/UserDao";
import { Language } from "@/data/model/Enums";
import type { IIdentity, IRole } from "@/data/model/User";
import { Validation } from "@/data/util/Validation";

import { languageSelectList } from "@/shared/localization/language";

import { i18n } from "@/portal/Trans";
import { AvatarPicker } from "@/portal/formControls/AvatarPicker";
import { DatePicker } from "@/portal/formControls/DatePicker";
import { Select } from "@/portal/formControls/Select";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";

export class UserForm extends BaseEntityForm<IIdentity, ICreateUserCmd> {
    private $passwordField?: JQuery;
    private $firstNameField: JQuery;
    private $lastNameField: JQuery;
    private $emailField: JQuery;
    private $zipField: JQuery;
    private $phoneNumberField: JQuery;
    private birthdayPicker: DatePicker;
    private avatarPicker?: AvatarPicker;
    private roleSelect?: Select<IRole>;
    private languageSelect: Select<Language>;
    private $pspUserIdField: JQuery;

    constructor(element: JQuery) {
        super(element, Factory.getInstance(UserDao));

        this.$passwordField = this.$form.find('.form-control[name="password"]');
        if (this.$passwordField.length === 0) this.$passwordField = undefined;

        this.$firstNameField = this.$form
            .find('.form-control[name="firstname"]')
            .change(() => this.updateName(this.$firstNameField.val() + " " + this.$lastNameField.val()));
        this.$lastNameField = this.$form
            .find('.form-control[name="lastname"]')
            .change(() => this.updateName(this.$firstNameField.val() + " " + this.$lastNameField.val()));

        const avatarPickerEl = this.$form.find("#useravatar");
        if (avatarPickerEl.length !== 0) this.avatarPicker = AvatarPicker.createFromFormCtrl(avatarPickerEl);

        this.$emailField = this.$form.find('.form-control[name="email"]');
        this.$phoneNumberField = this.$form.find('.form-control[name="phone"]');
        this.$zipField = this.$form.find('.form-control[name="zip"]');
        this.birthdayPicker = new DatePicker(this.$form.find('.form-control[name="birthday"]'), {
            endDate: "today",
        });
        this.$pspUserIdField = this.$form.find('.form-control[name="pspUserId"]');
        this.$pspUserIdField.addClass("disabled");
        
        const roleSelectorEl = this.$form.find('.form-control[name="userrole"]');
        if (roleSelectorEl.length !== 0)
            this.roleSelect = Select.createFromSelect(
                roleSelectorEl,
                Factory.getInstance<UserDao>(UserDao).userRoleDataSource,
                (c) => c.name,
                false,
                undefined,
                undefined,
                true
            );

        const langSelect = this.$form.find('.form-control[name="language"]');
        languageSelectList.forEach((item) => {
            langSelect.append(`<option value="${item.id}">${i18n(`enums.Language.${item.name}`)}</option>`);
        });
        this.languageSelect = Select.createFromSelect(langSelect);
    }

    public setFormData(model?: IIdentity) {
        this.updateName(model === undefined ? "" : model.firstName!);
        this.$firstNameField.val(model === undefined ? "" : model.firstName!);
        this.$lastNameField.val(model === undefined ? "" : model.lastName!);
        this.$emailField.val((model === undefined || model.user === undefined ? model?.email : model.user.email)!);
        this.$zipField.val(model === undefined ? "" : model.zip!);
        this.birthdayPicker.setValue(model === undefined ? undefined : model.birthday);
        this.languageSelect.setValue(model === undefined ? Language.English : model.language);

        if (this.$passwordField !== undefined) this.$passwordField.val("");

        if (this.avatarPicker !== undefined)
            this.avatarPicker.setValue(model === undefined ? undefined : model.avatarPath);

        if (this.roleSelect !== undefined && model !== undefined && model.user !== undefined)
            this.roleSelect.setValues(model.user.roles);
        
        this.$pspUserIdField.val(model?.walleeExternalCustomerId ?? "");
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateUserCmd {
        const phoneNumber = this.$phoneNumberField.val();
        const data = {
            firstName: this.$firstNameField.val().length > 0 ? this.$firstNameField.val() : undefined,
            lastName: this.$lastNameField.val().length > 0 ? this.$lastNameField.val() : undefined,
            email: this.$emailField.val().length > 0 ? this.$emailField.val() : undefined,
            zip: this.$zipField.val().length > 0 ? this.$zipField.val() : undefined,
            birthday: this.birthdayPicker.getValue(),
            roleIds: this.roleSelect !== undefined ? this.roleSelect.getValues() : [],
            avatar: this.avatarPicker !== undefined ? this.avatarPicker.getValue() : undefined,
            language: parseInt(this.languageSelect.getValue()!),
            phoneNumber: phoneNumber?.length > 0 ? phoneNumber : undefined,
            password:
                this.$passwordField === undefined
                    ? undefined
                    : this.$passwordField.val().length > 0
                      ? this.$passwordField.val()
                      : undefined,
        };

        let hasError = false;

        if (data.firstName === undefined) {
            hasError = true;
            this.$firstNameField.addClass("is-invalid");
        }

        if (data.lastName === undefined) {
            hasError = true;
            this.$lastNameField.addClass("is-invalid");
        }

        if ((this.id === undefined || data.email !== undefined) && !Validation.isEmail(data.email)) {
            hasError = true;
            this.$emailField.addClass("is-invalid");
        }

        if (this.id === undefined && !Validation.isPassword(data.password)) {
            hasError = true;
            this.$passwordField!.addClass("is-invalid");
        }

        if (data.zip !== undefined && !Validation.isZip(data.zip)) {
            hasError = true;
            this.$zipField.addClass("is-invalid");
        }

        if (this.roleSelect !== undefined) {
            if (data.roleIds === undefined || data.roleIds.length === 0) {
                hasError = true;
                this.roleSelect.setError(true);
            } else this.roleSelect.setError(false);
        }

        return (hasError ? undefined : data) as any;
    }
}
